import { reachGoal } from 'utils/metrica';
import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import every from "lodash/every";

import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import AccordionDetails from "@mui/material/AccordionDetails";

import {
  useDeclarationsByOrderIdQuery,
  useLazyDeclarationSummaryByOrderIdQuery,
  useLazyGetOrderSummaryByIdQuery,
  useSetCompletedByOrderIdMutation,
  useSummaryCompleted,
} from "../../../../../api/calc"
import {useGetCalcIdFromRoute} from "../../../../../utils/hooks";
import {useDemoCustomsAction} from 'components/cabinet/demo/steps/customs/use-customs';
import {COLORS} from "../../../../../config/constants";

import CustomsTitle from "./components/customsTitle";
import CustomsDetails from "./components/customsDetails";
import BrokerServices from "./components/broker_services";
import BrokerCommission from "./components/broker_commission";
import {Save} from '@vedmarket/ui-kit';
import Typography from '@mui/material/Typography';
import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';

import './customs.scss'
import Notification, {NOTIFICATION_TYPE} from "../../../../common/notification";
import {isCalculationViewModeError} from 'utils/helpers';
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../calculation-read-only';

const Customs = ({handleComplete}: CustomsProps) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()
  const [ getOrderSummaryRequest ] = useLazyGetOrderSummaryByIdQuery()
  const [ triggerFetchSummaryDeclaration ] = useLazyDeclarationSummaryByOrderIdQuery();
  const { data: declarations, refetch: refetchDeclaration, isUninitialized } =
      useDeclarationsByOrderIdQuery(orderId, { skip: !orderId });

  const [ goalDrafts, setGoalDrafts ] = useState<Record<string, Record<string, unknown>>>({});
  const [ collapsedInvoices, setCollapsedInvoices ] = useState<Record<string, boolean>>({});
  const handleGoalDraftChange = useCallback(({key, values}: CustomsGoalDraftChange) => {
    setGoalDrafts(current => {
      const next = {...current};
      if (values) next[key] = values;
      else delete next[key];
      return next;
    });
  }, []);

  const [ invalidVatCodes, setInvalidVatCodes ] = useState<Record<string, boolean>>({});
  const isConfirmButtonDisable = Object.values(invalidVatCodes).some(Boolean);

  const setCodeVatInvalid = useCallback(({key, invalid}: CustomsCodeVatValidity) => {
    setInvalidVatCodes(current => {
      if (Boolean(current[key]) === invalid) {
        return current;
      }

      return {...current, [key]: invalid};
    });
  }, []);

  const summaryCompleted = useSummaryCompleted()
  const declarationsCompleted = every(declarations, [ 'is_completed', true ]);
  const demoPlayback = useDemoCustomsAction()
  const canSaveCustoms = !declarationsCompleted || !summaryCompleted?.customs
  const readOnly = useCalculationReadOnly()
  const shouldShowSaveButton = (canSaveCustoms || demoPlayback.isDemoMode)

  useEffect(() => {
    if (!isUninitialized && orderId) {
      refetchDeclaration();
    }
  }, [ orderId ]);

  if (!declarations) {
    return null;
  }

  const refetchSummary = () => {
    triggerFetchSummaryDeclaration(orderId);
  }

  const renderOneDeclaration = (declaration: DeclarationsParamsOutput, index: number) => (

    <div className='customs'>
      <Accordion sx={{
        marginBottom: 0,
        border: `1px solid ${COLORS.SLIGHTLY_GRAY}`,
        boxShadow: 'none',
      }} defaultExpanded onChange={(_, expanded) => {
        setCollapsedInvoices(current => ({...current, [declaration.invoice_id]: !expanded}));
      }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon style={{
            width: '24px',
            height: '24px',
            fill: 'currentColor',
          }}/>}
          sx={{
            padding: '24px',
            minHeight: '99px',
            '&.Mui-expanded': {
              minHeight: '99px',
              paddingBottom: '23px',
            },
            '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              color: COLORS.LIGHT_ACTION,
              alignSelf: 'flex-start',
              marginTop: '1px',
            },
          }}
        >
          <CustomsTitle declaration={declaration} index={index}/>
        </AccordionSummary>
        <AccordionDetails className='customs__details' sx={{padding: '24px'}}>
          <CustomsDetails
            declaration={declaration}
            index={index}
            setCodeVatInvalid={setCodeVatInvalid}
            onGoalDraftChange={handleGoalDraftChange}
            refetchSummary={refetchSummary}
            refetchDeclaration={refetchDeclaration}
          />
        </AccordionDetails>
      </Accordion>
    </div>);

  const renderDeclarations = () => declarations.map((declaration, index) => <div key={index}>{renderOneDeclaration(declaration, index)}</div>);

  const handleSaveAll = async () => {
    const result = await setCompleted({
      orderId,
      customs: true,
    })

    if (isCalculationViewModeError(result) || 'error' in result) {
      return;
    }

    refetchDeclaration();
    getOrderSummaryRequest(orderId);
    handleComplete();
  }

  return <>
    {renderDeclarations()}
    <BrokerServices/>
    <BrokerCommission/>
    {isConfirmButtonDisable && <Notification
      type={NOTIFICATION_TYPE.WARNING}
      noCloseButton
      sx={{ marginTop: 2, marginBottom: 3 }}
      text={t('calc__text__customs__checkTheSelectionOfTheVATRateForEachHSCode')}
      title={t('common__message__warning')}
    />}
    {shouldShowSaveButton && <Button
      {...demoPlayback.buttonProps}
      variant='contained'
      fullWidth
      type='submit'
      className={cx({ 'read-only-action-button': readOnly })}
      startIcon={<Save />}
      endIcon={readOnlyButtonEndIcon(readOnly)}
      onClick={readOnly ? undefined : () => {
        reachGoal({
          goal: '4_tamozhna',
          params: {
            declaration_fields: Object.entries(goalDrafts)
              .filter(([ key ]) => !Object.entries(collapsedInvoices).some(([ invoiceId, collapsed ]) => collapsed && key.startsWith(`${invoiceId}-`)))
              .map(([ , fields ]) => fields),
          },
        });
        handleSaveAll();
      }}
      disabled={readOnly || isConfirmButtonDisable}
      title={readOnly ? t('calc__message__lockedByApplication') : undefined}
    ><Typography variant='inherit'>{formatDemoCopy(t('actions__text__save'))}</Typography></Button>}
  </>
}

export default Customs
